import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase.js'
import { configureApi } from '@/lib/api.js'
import { clearBranding } from '@/lib/branding.js'

/**
 * Supabase Auth session lifecycle (Phase 1b §3.1).
 *
 * Owns exactly three things and nothing else:
 *   1. restoring a session on boot (so a reload does not sign anyone out);
 *   2. keeping the in-memory session current as tokens refresh;
 *   3. signing out cleanly.
 *
 * Tenant, tier, branding and feature flags live in TenantProvider, sourced from
 * GET /api/me/features. Keeping them apart matters: the token says WHO you are
 * and never what your plan is, because Phase 0 §2 is explicit that baking tier
 * into a JWT leaves a downgraded tenant holding paid access until it expires.
 *
 * @typedef {object} AuthValue
 * @property {import('@supabase/supabase-js').Session|null} session
 * @property {boolean} initialising true until the stored session is resolved
 * @property {(email: string, password: string) => Promise<void>} signIn
 * @property {() => Promise<void>} signOut
 */

/** @type {React.Context<AuthValue>} */
const AuthContext = React.createContext(null)

export function AuthProvider ({ children }) {
  const [session, setSession] = React.useState(null)
  const [initialising, setInitialising] = React.useState(true)
  const queryClient = useQueryClient()

  /**
   * Sign out and leave nothing behind.
   *
   * Clearing the query cache is NOT optional housekeeping. Without it, the next
   * person to sign in on a shared work laptop — which is the normal case in a
   * contracting office — sees the previous tenant's clients and jobs rendered
   * from cache before their own data arrives. It looks exactly like a
   * cross-tenant leak, and telling a customer it was "only cached" is not a
   * conversation worth having.
   *
   * Branding is reset for the same reason: another tenant's colours on the
   * login screen reads as the same bug.
   */
  const signOut = React.useCallback(async () => {
    await supabase.auth.signOut()
    queryClient.clear()
    clearBranding()
    setSession(null)
  }, [queryClient])

  // Let the API client end a session it discovers is dead mid-request, without
  // lib/api.js having to import React or reach into a context.
  React.useEffect(() => {
    configureApi({
      onSessionLost: () => {
        queryClient.clear()
        clearBranding()
        setSession(null)
      }
    })
  }, [queryClient])

  React.useEffect(() => {
    let active = true

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session ?? null)
      setInitialising(false)
    })

    // Fires on refresh, sign-in and sign-out — including in another tab, which
    // is how a session stays consistent across a window someone left open.
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (!active) return
      setSession(next ?? null)
      if (event === 'SIGNED_OUT') {
        queryClient.clear()
        clearBranding()
      }
    })

    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [queryClient])

  const signIn = React.useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  /**
   * Password reset (Phase 2 item 18a — D28b). Supabase Auth end to end, no
   * hand-rolled token generation (brief, verbatim): this call is the entire
   * "forgot password" half. Always resolves without revealing whether the
   * address has an account — same posture LoginPage already takes on a
   * failed sign-in, and Supabase's own API does not distinguish either.
   */
  const requestPasswordReset = React.useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) throw error
  }, [])

  const value = React.useMemo(
    () => ({ session, initialising, signIn, signOut, requestPasswordReset }),
    [session, initialising, signIn, signOut, requestPasswordReset]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** @returns {AuthValue} */
export function useAuth () {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
